import { useEffect, useState } from "react";
import { showNotification } from "@mantine/notifications";
import {
  attachFileToParent,
  deleteFileApi,
  downloadViaProxy,
  getDownloadUrl,
  getParentFiles,
  getPreSignedUrl,
  getReadPresignedUrl,
} from "../utilities/apis/commonApis";
import { loadingStates } from "../utilities/utilities";
import { apiWrapWithErrorWithData } from "../utilities/apiHelpers";

const useMultiFileUpload = ({
  loadFromParent = true,
  parent,
  parentId,
  existingFiles = [],
  attachToParent = true,
  onFileCheck,
}) => {
  const commonAttributes = {
    downloading: loadingStates.NO_ACTIVE_REQUEST,
    attaching: loadingStates.NO_ACTIVE_REQUEST,
    attached: false,
  };
  const [uiConfigs, setUiConfigs] = useState({
    loadingFromParent: loadingStates.NO_ACTIVE_REQUEST,
  });
  const [files, setFiles] = useState(
    existingFiles.map((file) => ({
      fileId: file.id || null,
      url: file.hostedLink,
      uploaded: true,
      uploadStarted: true,
      fileName: file.fileName,
      extension: file.extension,
      fetchingUploadUrl: false,
      mimeType: file.mimeType,
      uploadPercentage: 100,
      errored: false,
      ...commonAttributes,
      attached: file.id !== undefined,
    })),
  );

  const addFiles = async (incomingFiles) => {
    const oldFilesLength = files.length;
    const newFiles = incomingFiles.map((item) => {
      const file = item?.file instanceof File ? item.file : item;
      let ext = file.name.split(".");
      ext = ext[ext.length - 1];
      return {
        url: null,
        uploaded: false,
        uploadStarted: false,
        fileName: file.name,
        extension: ext,
        mimeType: file.type,
        uploadPercentage: 0,
        fetchingUploadUrl: true,
        fileData: file,
        field: item?.field || null,
        errored: false,
        ...commonAttributes,
      };
    });

    const mergedFiles = [...files, ...newFiles];

    setFiles(mergedFiles);

    const response = await Promise.allSettled(
      newFiles.map((file) =>
        getPreSignedUrl({
          fileName: file.fileName,
          parentId,
          field: file.field,
        }),
      ),
    );

    // eslint-disable-next-line no-unused-vars
    const newFileMap = response.map((promise, index) => {
      const mergedIndex = oldFilesLength + index;
      const allFiles = [...mergedFiles];
      if (response[index].status === "fulfilled") {
        allFiles[mergedIndex].fetchingUploadUrl = false;
        allFiles[mergedIndex].signedUrl = response[index].value.data.signedUrl;
        allFiles[mergedIndex].url = response[index].value.data.destination;
      } else {
        allFiles[mergedIndex].errored = true;
      }
      setFiles(allFiles);
      return allFiles;
    });

    return response?.[0].status;
  };

  const startUpload = () => {
    files.forEach((file, index) => {
      const isUploadable =
        !file.uploaded &&
        !file.uploadStarted &&
        !file.fetchingUploadUrl &&
        !file.errored;

      if (!isUploadable) return;

      const reader = new FileReader();

      reader.addEventListener("load", async () => {
        const arrayBuffer = reader.result;
        if (!arrayBuffer) return;

        const fileSize = arrayBuffer.byteLength;

        try {
          const response = await fetch(`${file.signedUrl}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/octet-stream",
              "Content-Length": fileSize.toString(),
              "Content-Range": `bytes 0-${fileSize - 1}/${fileSize}`,
            },
            body: arrayBuffer,
          });

          const result = await response.json();

          const fileId = result.id;

          setFiles((filesInState) => {
            const newFiles = [...filesInState];
            newFiles[index].uploadPercentage = 100;
            newFiles[index].uploadStarted = true;
            return newFiles;
          });
          const newFiles = [...files];
          newFiles[index].uploaded = true;
          newFiles[index].fileData = null;
          if (parent && parentId && attachToParent) {
            newFiles[index].attaching = loadingStates.LOADING;
          }
          setFiles(newFiles);

          if (parent && parentId && attachToParent) {
            const resp = await apiWrapWithErrorWithData(
              attachFileToParent({
                parentId,
                parent,
                fileName: file.fileName,
                extension: file.extension,
                mimeType: file.mimeType,
                hostedLink: fileId, // or response.request.responseURL
                field: file.field,
              }),
            );

            const updatedFiles = [...newFiles];
            if (resp.success && resp.file.id) {
              updatedFiles[index].attached = true;
              updatedFiles[index].fileId = resp.file.id;
              updatedFiles[index].hostedLink = resp.file.hostedLink;
            } else {
              updatedFiles[index].errored = true;
              updatedFiles[index].attached = false;
            }
            updatedFiles[index].attaching = loadingStates.NO_ACTIVE_REQUEST;
            setFiles(updatedFiles);
          }
        } catch (err) {
          const failedFiles = [...files];
          failedFiles[index].errored = true;
          failedFiles[index].uploaded = false;
          setFiles(failedFiles);
        }
      });

      reader.readAsArrayBuffer(file.fileData); // Start reading the file
      setFiles((filesInState) => {
        const newFiles = [...filesInState];
        newFiles[index].uploadStarted = true;
        newFiles[index].uploadPercentage = 0;
        return newFiles;
      });
    });
  };

  useEffect(startUpload, [files]);

  const deleteFile = async (indexToDelete) => {
    const fileToDelete = files[indexToDelete];
    if (!fileToDelete) return false;

    setUiConfigs((prev) => ({
      ...prev,
      loadingFromParent: loadingStates.LOADING,
    }));

    try {
      if (fileToDelete.attached && fileToDelete.fileId) {
        const resp = await apiWrapWithErrorWithData(
          deleteFileApi({ fileId: fileToDelete.fileId }),
        );
        if (!resp.success) {
          showNotification({
            title: "Deletion Failed",
            message: "File deletion failed.",
            color: "red",
          });
          return false;
        }
      }

      setFiles((prev) => prev.filter((_, index) => index !== indexToDelete));
      return true;
    } finally {
      setUiConfigs((prev) => ({
        ...prev,
        loadingFromParent: loadingStates.NO_ACTIVE_REQUEST,
      }));
    }
  };

  const checkFile = (indexToCheck) => () => {
    // const newFiles = files.map(
    //   (file, index) => ({ ...file, checked: index === indexToCheck }),
    // );
    if (onFileCheck && !files[indexToCheck].checked) {
      if (!onFileCheck(files[indexToCheck], files)) {
        setFiles((filesInState) =>
          filesInState.map((file, index) => ({
            ...file,
            checked: !!filesInState[index].checked,
          })),
        );
        return;
      }
    }
    setFiles((filesInState) =>
      filesInState.map((file, index) => ({
        ...file,
        checked:
          index === indexToCheck
            ? !filesInState[index].checked
            : !!filesInState[index].checked,
      })),
    );
  };

  const finalFiles = files
    .filter((file) => file.uploaded)
    .map((file) => ({
      fileId: file.fileId || null,
      fileName: file.fileName,
      createdAt: file.createdAt,
      extension: file.extension,
      mimeType: file.mimeType,
      hostedLink: file.url,
      attached: file.attached,
      checked: file.checked,
    }));

  const triggerBrowserDownload = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const saveDownloadFile = (index) => async () => {
    const file = files[index];
    if (!file?.url) return;

    setFiles((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], saving: loadingStates.LOADING };
      return updated;
    });

    try {
      const response = await apiWrapWithErrorWithData(
        getDownloadUrl({ hostedLink: file.url }),
      );
      if (!response?.success || !response?.downloadUrl) {
        throw new Error("Failed to get download URL");
      }

      const { data, contentType } = await downloadViaProxy(
        response.downloadUrl,
      );
      triggerBrowserDownload(
        new Blob([data], { type: contentType }),
        file.fileName,
      );
    } catch {
      showNotification({
        color: "red",
        title: "Download",
        message: "Could not download the file.",
      });
    } finally {
      setFiles((prevFiles) => {
        const updatedFiles = [...prevFiles];
        if (updatedFiles[index]) {
          updatedFiles[index].saving = loadingStates.NO_ACTIVE_REQUEST;
        }
        return updatedFiles;
      });
    }
  };

  const downloadFile = (index) => async () => {
    const file = files[index];
    if (!file?.url) return;

    if (file.url.startsWith("https://dr")) {
      window.open(file.url, "_blank")?.focus();
      return;
    }

    setFiles((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], downloading: loadingStates.LOADING };
      return updated;
    });

    const response = await apiWrapWithErrorWithData(
      getReadPresignedUrl({
        hostedLink: file.url,
        parentId,
        parent,
      }),
    );

    setFiles((prev) => {
      const updated = [...prev];
      if (updated[index]) {
        updated[index].downloading = loadingStates.NO_ACTIVE_REQUEST;
      }
      return updated;
    });

    if (response?.success && response?.signedUrl) {
      window.open(response.signedUrl, "_blank")?.focus();
    }
  };

  const addUploadedFiles = (uploadedFiles) => {
    setFiles((stateFiles) => {
      const allFilesUrl = stateFiles
        .map((file) => file.url)
        .filter((url) => !!url);
      const incomingFiles = uploadedFiles.filter(
        (file) => !allFilesUrl.includes(file.hostedLink),
      );
      const newFiles = [
        ...stateFiles,
        ...incomingFiles.map((file) => ({
          fileId: file.id || null,
          url: file.hostedLink,
          createdAt: file.createdAt,
          uploaded: true,
          uploadStarted: true,
          fileName: file.fileName,
          extension: file.extension,
          fetchingUploadUrl: false,
          mimeType: file.mimeType,
          field: file.field || null,
          uploadPercentage: 100,
          errored: false,
          ...commonAttributes,
          attached: file.id !== undefined,
        })),
      ];

      return newFiles;
    });
  };

  const resetUploadedFiles = () => {
    setFiles([]);
  };

  useEffect(() => {
    (async () => {
      if (loadFromParent && parentId && parent) {
        setUiConfigs({
          ...uiConfigs,
          loadingFromParent: loadingStates.LOADING,
        });
        const resp = await apiWrapWithErrorWithData(
          getParentFiles({
            parentId,
            parent,
          }),
        );
        if (resp?.success && resp?.files) {
          addUploadedFiles(resp.files);
        }
        setUiConfigs({
          ...uiConfigs,
          loadingFromParent: loadingStates.NO_ACTIVE_REQUEST,
        });
      }
    })();
    return () => {
      setFiles([]);
    };
  }, []);

  const unCheckAllFiles = () => {
    setFiles((prevStateFieles) =>
      prevStateFieles.map((stFile) => ({
        ...stFile,
        checked: false,
      })),
    );
  };

  return {
    addFiles,
    addUploadedFiles,
    deleteFile,
    checkFile,
    downloadFile,
    saveDownloadFile,
    resetUploadedFiles,
    unCheckAllFiles,
    finalFiles,
    files,
    loadingFromParent: uiConfigs.loadingFromParent,
  };
};

export default useMultiFileUpload;
